import { useInfiniteGetMacroAssortmentQuery, GetMacroAssortmentInput } from '@graphql/generated/graphql';
import { useEffect, useMemo } from 'react';
import logger from '@utils/logger';

const useGetMacroAssortment = (input: GetMacroAssortmentInput, options?: { enabled?: boolean }) => {
  const { enabled = true } = options ?? {};
  const { ...restInput } = input;

  const { data, error, isError, isLoading, fetchNextPage, hasNextPage } = useInfiniteGetMacroAssortmentQuery(
    'input',
    {
      input: {
        ...restInput,
      },
    },
    {
      staleTime: 0,
      cacheTime: 0,
      refetchInterval: false,
      // disable refetchOnWindowFocus so that data is not refreshed
      // when user leave the page and come back
      refetchOnWindowFocus: false,
      enabled,
      getNextPageParam: (lastPage) => {
        const {
          getMacroAssortment: { paginationToken },
        } = lastPage;
        if (!paginationToken) {
          return undefined;
        }
        return {
          input: {
            ...restInput,
            paginationToken,
          },
        };
      },
      retry: false,
    },
  );

  const macroAssortmentResult = useMemo(
    () => data?.pages.flatMap((page) => page.getMacroAssortment.assortment ?? []),
    [data?.pages],
  );

  useEffect(() => {
    if (isError) {
      const { message, name } = error as Error;
      void logger.error(`Error occurred while retrieving assortment entries: ${name}, ${message}`);
    }
  }, [isError, error]);

  return {
    isLoading,
    macroAssortmentResult,
    hasNextPage,
    fetchNextPage,
  };
};

export default useGetMacroAssortment;
